// NOTE: NSAI-5657 / NSAI-5658 — saved dashboards: an HTML page generated in
// chat, kept under a name, versioned, shared, and (when it is live) refreshed
// by re-running the request that produced its data. These are the wire types
// web and mobile both read; the server mirrors them in src/dashboards.

// NOTE: The caller's access to one dashboard, decided by the server on every
// request. Clients never re-derive it from owner uuids.
export enum EDashboardRole {
  OWNER = "owner",
  EDITOR = "editor",
  VIEWER = "viewer",
}

export enum EDashboardShareRole {
  VIEWER = "viewer",
  EDITOR = "editor",
}

// NOTE: LIVE — the dashboard keeps the request its data came from and can be
// refreshed. STATIC — a snapshot; what was saved is what everyone sees.
export enum EDashboardDataMode {
  LIVE = "live",
  STATIC = "static",
}

export enum EDashboardRefreshStatus {
  RUNNING = "running",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
}

export enum EDashboardRefreshTrigger {
  OPEN = "open",
  MANUAL = "manual",
  SCHEDULE = "schedule",
}

export enum EDashboardScheduleFrequency {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
}

export enum EDashboardVersionKind {
  SAVED = "saved",
  RESTORED = "restored",
  DUPLICATED = "duplicated",
}

// NOTE: Why a refresh did not produce new data. Each has a sentence under
// `dashboards:refresh.errors.<code>`; `errorMessage` carries the run's own
// wording when there is one.
export enum EDashboardRefreshErrorCode {
  SOURCE_UNAVAILABLE = "source_unavailable",
  INVALID_OUTPUT = "invalid_output",
  TIMEOUT = "timeout",
  BUDGET_EXCEEDED = "budget_exceeded",
  PROVIDER_BUDGET_EXCEEDED = "provider_budget_exceeded",
  UNPRICED_MODEL = "unpriced_model",
  RATE_LIMITED = "rate_limited",
  MODEL_UNAVAILABLE = "model_unavailable",
  NO_ACCESS = "no_access",
  TOO_LARGE = "too_large",
  INTERRUPTED = "interrupted",
  UNKNOWN = "unknown",
}

// NOTE: Where the page on screen came from. NONE means there is nothing the
// caller may see yet: a live dashboard someone else built has to be refreshed
// with the caller's own access before it shows anything (the viewer's data
// permissions win over the author's).
export enum EDashboardContentSource {
  VERSION = "version",
  REFRESH = "refresh",
  NONE = "none",
}

// NOTE: Tabs of the Dashboards list. Filtering happens on the client over the
// one list response — names are encrypted at rest, so the server cannot search
// them anyway.
export enum EDashboardListFilter {
  ALL = "all",
  MINE = "mine",
  SHARED = "shared",
  ARCHIVED = "archived",
}

export interface IDashboardPerson {
  userUuid: string;
  firstName: string | null;
  lastName: string | null;
}

export interface IDashboardScheduleInput {
  frequency: EDashboardScheduleFrequency;
  // NOTE: 0-23 / 0-59, wall-clock time in `timezone`.
  hour: number;
  minute: number;
  // NOTE: 0 (Sunday) - 6, WEEKLY only.
  dayOfWeek?: number | null;
  // NOTE: 1-28, MONTHLY only — capped so every month has the day.
  dayOfMonth?: number | null;
  // NOTE: IANA zone, e.g. "Europe/Berlin".
  timezone: string;
}

export interface IDashboardSchedule {
  frequency: EDashboardScheduleFrequency;
  hour: number;
  minute: number;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  timezone: string;
  // NOTE: ISO 8601; null only while the schedule cannot fire.
  nextRunAt: string | null;
}

export interface IDashboardRefreshSummary {
  uuid: string;
  status: EDashboardRefreshStatus;
  trigger: EDashboardRefreshTrigger;
  // NOTE: Integration providers the run used ("jira", "google_drive"…), for
  // the "Source" line. Empty for a run that used none.
  sources: string[];
  errorCode: EDashboardRefreshErrorCode | null;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface IDashboardListItem {
  uuid: string;
  name: string;
  role: EDashboardRole;
  owner: IDashboardPerson;
  dataMode: EDashboardDataMode;
  refreshOnOpen: boolean;
  schedule: IDashboardSchedule | null;
  isPinned: boolean;
  isTeamPinned: boolean;
  teamAccessRole: EDashboardShareRole | null;
  // NOTE: Owner only: whether anyone else can open it (a member share or team
  // access). Always false for a recipient.
  isShared: boolean;
  isArchived: boolean;
  currentVersionNumber: number;
  // NOTE: The CALLER's latest refresh, any status — each person refreshes with
  // their own access, so there is no shared "last refresh".
  lastRefresh: IDashboardRefreshSummary | null;
  lastSuccessfulRefreshAt: string | null;
  // NOTE: The conversation the dashboard is edited in. Owner only — a chat is
  // private to its owner.
  chatUuid: string | null;
  lastOpenedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IDashboardPermissions {
  canEdit: boolean;
  canShare: boolean;
  canArchive: boolean;
  canDelete: boolean;
  canDuplicate: boolean;
  canRefresh: boolean;
  canTeamPin: boolean;
  canSchedule: boolean;
}

export interface IDashboardVersion {
  uuid: string;
  number: number;
  kind: EDashboardVersionKind;
  restoredFromNumber: number | null;
  createdAt: string;
  createdBy: IDashboardPerson | null;
  isCurrent: boolean;
  // NOTE: False when the version holds data the caller did not pull
  // themselves (a live dashboard's version saved by someone else).
  canPreview: boolean;
}

export interface IDashboardDetails extends IDashboardListItem {
  // NOTE: Owner and editors only; null for a viewer and for a static dashboard.
  dataRequest: string | null;
  permissions: IDashboardPermissions;
  currentVersion: IDashboardVersion;
}

export interface IDashboardContent {
  html: string | null;
  source: EDashboardContentSource;
  versionUuid: string;
  versionNumber: number;
  // NOTE: When the data on screen was produced; null when nothing is shown.
  dataAsOf: string | null;
  lastRefresh: IDashboardRefreshSummary | null;
  // NOTE: Server-decided: the client starts a refresh as soon as it renders.
  shouldRefreshOnOpen: boolean;
  // NOTE: NSAI-6924 — the data on screen was pulled before the data request
  // was last edited; the status line says so instead of calling it current.
  isDataForOldRequest: boolean;
}

export interface IDashboardVersionContent {
  html: string;
}

export interface IDashboardShareMember extends IDashboardPerson {
  email: string;
}

export interface IDashboardShare {
  uuid: string;
  role: EDashboardShareRole;
  member: IDashboardShareMember;
  // NOTE: Advisory: whether the member holds dashboard:write today. The server
  // re-checks on every write.
  canReceiveEditorRole: boolean;
  createdAt: string;
}

export interface IDashboardSharesResponse {
  teamAccessRole: EDashboardShareRole | null;
  shares: IDashboardShare[];
}

export interface IDashboardShareCandidate extends IDashboardShareMember {
  canReceiveEditorRole: boolean;
}

export interface ICreateDashboardRequest {
  // NOTE: The generated page (a text/html chat file) the dashboard starts from.
  fileUuid: string;
  name: string;
  // NOTE: Empty or omitted saves a static dashboard.
  dataRequest?: string | null;
  refreshOnOpen?: boolean;
  schedule?: IDashboardScheduleInput | null;
}

export interface IUpdateDashboardRequest {
  name?: string;
  dataRequest?: string | null;
  refreshOnOpen?: boolean;
}

export interface IUpdateDashboardScheduleRequest {
  schedule: IDashboardScheduleInput | null;
}

export interface IAddDashboardVersionRequest {
  fileUuid: string;
}

export interface IDuplicateDashboardRequest {
  name: string;
}

export interface IRefreshDashboardRequest {
  trigger: EDashboardRefreshTrigger.OPEN | EDashboardRefreshTrigger.MANUAL;
}

export interface ISetDashboardPinRequest {
  isPinned: boolean;
}

export interface ISetDashboardTeamPinRequest {
  isTeamPinned: boolean;
}

export interface ISetDashboardTeamAccessRequest {
  role: EDashboardShareRole | null;
}

export interface ICreateDashboardShareRequest {
  userUuid: string;
  role: EDashboardShareRole;
}

export interface IUpdateDashboardShareRequest {
  role: EDashboardShareRole;
}

export interface IDashboardDeleteResponse {
  success: boolean;
}

// NOTE: Client-side API call props — one object argument per call.
export interface IDashboardUuidProps {
  dashboardUuid: string;
}

export interface IGetDashboardContentProps extends IDashboardUuidProps {
  isPreview?: boolean;
}

export interface IListDashboardsProps {
  isArchived?: boolean;
}

export interface IUpdateDashboardProps extends IDashboardUuidProps {
  payload: IUpdateDashboardRequest;
}

export interface IUpdateDashboardScheduleProps extends IDashboardUuidProps {
  payload: IUpdateDashboardScheduleRequest;
}

export interface IAddDashboardVersionProps extends IDashboardUuidProps {
  payload: IAddDashboardVersionRequest;
}

export interface IDuplicateDashboardProps extends IDashboardUuidProps {
  payload: IDuplicateDashboardRequest;
}

export interface IRefreshDashboardProps extends IDashboardUuidProps {
  payload: IRefreshDashboardRequest;
}

export interface ISetDashboardPinProps extends IDashboardUuidProps {
  payload: ISetDashboardPinRequest;
}

export interface ISetDashboardTeamPinProps extends IDashboardUuidProps {
  payload: ISetDashboardTeamPinRequest;
}

export interface ISetDashboardTeamAccessProps extends IDashboardUuidProps {
  payload: ISetDashboardTeamAccessRequest;
}

export interface IDashboardVersionProps extends IDashboardUuidProps {
  versionUuid: string;
}

export interface ICreateDashboardShareProps extends IDashboardUuidProps {
  payload: ICreateDashboardShareRequest;
}

export interface IUpdateDashboardShareProps extends IDashboardUuidProps {
  shareUuid: string;
  payload: IUpdateDashboardShareRequest;
}

export interface IRevokeDashboardShareProps extends IDashboardUuidProps {
  shareUuid: string;
}

export interface IListDashboardShareCandidatesProps extends IDashboardUuidProps {
  search?: string;
}

// NOTE: Pure-helper props (utils/dashboards.ts).
export interface IFilterDashboardsProps {
  dashboards: IDashboardListItem[];
  filter: EDashboardListFilter;
  search: string;
}

export interface IBuildDashboardDataRequestFile {
  type: string;
  name?: string;
}

export interface IBuildDashboardDataRequestMessage {
  uuid: string;
  role: string;
  content: string;
  // NOTE: NSAI-6928 — the message's files, so the prefill can tell a reply
  // that held a page from one that did not. Optional: without it every reply
  // reads as page-less.
  files?: ReadonlyArray<IBuildDashboardDataRequestFile>;
}

export interface IFindOriginatingRequestIndexProps {
  messages: IBuildDashboardDataRequestMessage[];
  pageIndex: number;
}

export interface IBuildDashboardDataRequestProps {
  messages: IBuildDashboardDataRequestMessage[];
  // NOTE: The assistant message that carries the page.
  messageUuid: string;
}

export interface IShouldWarnDashboardSnapshotProps {
  dashboard: Pick<IDashboardListItem, "dataMode" | "isShared">;
  isLive: boolean;
}

export interface IResolveDashboardRefreshPollIntervalProps {
  refresh: IDashboardRefreshSummary | null | undefined;
  error: unknown;
}

export interface IIsDashboardRefreshStaleProps {
  dataAsOf: string | null;
  now?: Date;
}

export interface IFormatDashboardPersonNameProps {
  person: IDashboardPerson | null;
  fallback: string;
}

export interface IFindDashboardForChatProps {
  dashboards: IDashboardListItem[];
  chatUuid: string | undefined;
}

export interface IDashboardSubUrlProps {
  dashboardUuid: string;
  segment: string;
}

export interface IBuildDefaultDashboardScheduleProps {
  frequency: EDashboardScheduleFrequency;
  timezone: string;
}
